import { ChangeEvent, useCallback, useRef } from 'react'

import { nanoid } from 'nanoid'

import { fileSize } from '@bettermode/common/filesize'
import {
  useNetwork,
  useFileUploader,
  useImageUploader,
} from '@tribeplatform/react-sdk/hooks'
import { ButtonSize } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useI18n } from '../../i18n/index.js'
import { ChatAttachment } from '../../Messaging/types.js'

import { ComposerIconButton } from './ComposerIconButton.js'

interface Props {
  size?: ButtonSize
  disabled?: boolean
  type: ChatAttachment['type']
  onFileSelected?: (files: File[], type: ChatAttachment['type']) => void
}
export type AttachmentPickerButtonRef = {
  open: () => void
}

export const ChatAttachmentPickerButton = ({
  size,
  onFileSelected,
  disabled,
  type = 'file',
}: Props) => {
  const { data: network } = useNetwork()
  const { fileTypesLimit, imageTypesLimit } = network?.settings || {}
  const acceptedFileTypes = fileTypesLimit?.join(',')
  const acceptedImageTypes = imageTypesLimit?.join(',') ?? 'image/*'
  const acceptedTypes = type === 'file' ? acceptedFileTypes : acceptedImageTypes
  const attachmentInputId = `attachment-input-${nanoid(10)}`
  const hiddenFileInput = useRef<HTMLInputElement>()
  const { getLimitExceedingFiles } = useFileUploader()
  const { getLimitExceedingImages } = useImageUploader()
  const { $t } = useI18n()

  const getIsSizeLimitExceeded = useCallback(
    (files: File[]) => {
      if (type === 'file') {
        const { sizeLimitExceededFiles, limit } = getLimitExceedingFiles(files)
        return {
          isSizeLimitExceeded: sizeLimitExceededFiles.length > 0,
          limit,
        }
      }

      const { sizeLimitExceededImages, limit } = getLimitExceedingImages(files)
      return {
        isSizeLimitExceeded: sizeLimitExceededImages.length > 0,
        limit,
      }
    },
    [getLimitExceedingFiles, getLimitExceedingImages, type],
  )

  const onAttachmentInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files) {
        return
      }

      const files = Array.from(event.target.files)
      const { isSizeLimitExceeded, limit } = getIsSizeLimitExceeded(files)

      if (isSizeLimitExceeded) {
        toast({
          title: $t({
            id: 'Generics.Error',
            defaultMessage: 'Error',
          }),
          description: $t(
            {
              id: 'Generics.FileSizeLimit',
              defaultMessage: 'The file size limit is {size_limit}.',
            },
            { size_limit: fileSize(limit) },
          ),
          status: 'error',
        })
        return
      }

      onFileSelected?.(files, type)
      event.target.value = ''
    },
    [getIsSizeLimitExceeded, onFileSelected, type, $t],
  )

  return (
    <div>
      <ComposerIconButton
        icon={<SvgIcon name={type === 'file' ? 'attachment' : 'image'} />}
        aria-label="Attachment"
        onClick={() => hiddenFileInput.current?.click()}
        size={size}
        disabled={disabled}
      />
      <input
        onChange={onAttachmentInputChange}
        id={attachmentInputId}
        ref={hiddenFileInput}
        accept={acceptedTypes}
        type="file"
        className="hidden"
        multiple
      />
    </div>
  )
}
